import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, Star, ShieldCheck, Package, LogOut } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import CategoryStrip from "@/components/CategoryStrip";

const Header = () => {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const { totalQty } = useCart();
  const { user, logout } = useAuth();

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  const startGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <>
      <header
        className="sticky top-0 z-50 bg-[#C62828] text-white fk-shadow"
        data-testid="site-header"
      >
        <div className="max-w-[1400px] mx-auto flex items-center gap-4 px-3 sm:px-6 h-14">
          <Link to="/" className="flex flex-col leading-none" data-testid="logo-link">
            <span className="font-head text-xl font-bold italic tracking-tight" data-testid="site-header-logo-text">
              BharatiCrafts
            </span>
            <span className="flex items-center gap-0.5 text-[11px] italic text-gray-200">
              Explore <span className="text-[#ffe500] font-medium">Plus</span>
              <Star className="w-2.5 h-2.5 fill-[#ffe500] text-[#ffe500]" />
            </span>
          </Link>

          <form
            onSubmit={submit}
            className="flex-1 max-w-2xl relative"
            data-testid="search-form"
          >
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search for products, brands and more"
              className="w-full h-9 rounded-sm pl-4 pr-10 text-sm text-gray-800 placeholder:text-gray-400 outline-none"
              data-testid="search-input"
            />
            <button
              type="submit"
              className="absolute right-0 top-0 h-9 w-10 flex items-center justify-center text-[#C62828]"
              data-testid="search-submit"
              aria-label="Search"
            >
              <Search className="w-5 h-5" />
            </button>
          </form>

          <Link
            to="/my-orders"
            className="hidden sm:flex items-center gap-1.5 font-medium"
            data-testid="header-my-orders-link"
          >
            <Package className="w-5 h-5" />
            <span className="hidden md:inline">My Orders</span>
          </Link>

          <Link
            to="/cart"
            className="relative flex items-center gap-2 font-medium"
            data-testid="cart-link"
          >
            <div className="relative">
              <ShoppingCart className="w-6 h-6" />
              {totalQty > 0 && (
                <span
                  className="absolute -top-2 -right-2 bg-[#ff6161] text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center"
                  data-testid="cart-badge"
                >
                  {totalQty}
                </span>
              )}
            </div>
            <span className="hidden sm:inline">Cart</span>
          </Link>

          {user ? (
            <div className="flex items-center gap-2" data-testid="user-menu">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full" referrerPolicy="no-referrer" />
              ) : null}
              <span className="hidden lg:inline text-sm font-medium max-w-[90px] truncate">
                {user.name?.split(" ")[0]}
              </span>
              <button
                onClick={logout}
                className="flex items-center gap-1 text-xs font-medium opacity-90 hover:opacity-100"
                data-testid="sign-out-btn"
              >
                <LogOut className="w-4 h-4" /> <span className="hidden md:inline">Sign out</span>
              </button>
            </div>
          ) : (
            <button
              onClick={startGoogleLogin}
              className="bg-white text-[#C62828] font-semibold text-sm px-4 sm:px-6 h-8 rounded-sm hover:bg-gray-100 transition-colors"
              data-testid="login-btn"
            >
              Login
            </button>
          )}

          <Link
            to="/admin/products"
            className="hidden md:flex items-center gap-1 text-xs font-medium opacity-80 hover:opacity-100"
            data-testid="header-admin-nav-link"
          >
            <ShieldCheck className="w-4 h-4" /> Admin
          </Link>
        </div>
      </header>
      <CategoryStrip />
    </>
  );
};

export default Header;
